/**
 * Monaco editor bootstrap.
 *
 * By default, `@monaco-editor/react` lazily fetches the `monaco-editor` runtime
 * from the jsdelivr CDN at first render. The download has no integrity check,
 * so a compromise of jsdelivr would compromise the portal.
 *
 * To mitigate this, we configure the `@monaco-editor/loader` to use the
 * `monaco-editor` package installed locally from npm. Vite then bundles the
 * editor sources (and its web workers) as first-party assets served from the
 * portal's own origin — no third-party CDN is contacted at runtime.
 *
 * This module must be imported once at app startup, before any Monaco-backed
 * component (e.g. `FileEditorModal`) renders.
 *
 * @module config/monaco
 */
import { loader } from '@monaco-editor/react';
import * as monaco from 'monaco-editor';
import editorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker';
import jsonWorker from 'monaco-editor/esm/vs/language/json/json.worker?worker';
import cssWorker from 'monaco-editor/esm/vs/language/css/css.worker?worker';
import htmlWorker from 'monaco-editor/esm/vs/language/html/html.worker?worker';
import tsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker?worker';

/**
 * Wires up Vite's `?worker` imports to Monaco. Monaco calls
 * `MonacoEnvironment.getWorker` whenever it needs a language service worker;
 * returning the locally-bundled worker prevents Monaco from falling back to
 * any CDN-hosted worker URL.
 */
self.MonacoEnvironment = {
  getWorker(_workerId, label) {
    switch (label) {
      case 'json':
        return new jsonWorker();
      case 'css':
      case 'scss':
      case 'less':
        return new cssWorker();
      case 'html':
      case 'handlebars':
      case 'razor':
        return new htmlWorker();
      case 'typescript':
      case 'javascript':
        return new tsWorker();
      default:
        return new editorWorker();
    }
  },
};

/**
 * Tell `@monaco-editor/loader` to use the npm-installed `monaco-editor`
 * instead of downloading it from jsdelivr. This must run before any
 * Monaco-backed component renders.
 */
loader.config({ monaco });
